import { getBrowserBridgeAPI } from '@atlassian/rovo-content-bridge-api/singleton';
import type { ClientId } from '@atlassian/rovo-content-bridge-api/client-types';
import { InlineInputBoxSendMessageCommand } from '@atlassian/rovo-content-bridge-api-commands/commands/inline-input-box-send-message-command';
import { ROVO_CHAT_CLIENT_ID } from '@atlassian/rovo-content-bridge-api-commands/clients/rovo-chat-client';
import type { RovoContentBridgeSetupHandler } from '@atlassian/rovo-content-bridge-api/create-bridge-client-types';
import { useChatContextStoreActions } from '@atlassian/conversation-assistant-store/controllers/chat-context/store';
import { useCallback } from 'react';
import { BoeSubmitPrompt } from './commands/boe-submit-prompt.ts';

// Detect BPOE content type from bridge `senderId` (databases use a
// `confluence-database-{contentId}` prefix; whiteboards use `confluence-whiteboards`).
// Returns `undefined` when the senderId is missing or unrecognised so the caller
// can skip target registration rather than guess a type.
export const inferContentTypeFromSenderId = (
	senderId: string | undefined,
): 'whiteboard' | 'database' | undefined => {
	if (!senderId) {
		return undefined;
	}
	if (senderId.startsWith('confluence-database')) {
		return 'database';
	}
	if (senderId.startsWith('confluence-whiteboards')) {
		return 'whiteboard';
	}
	return undefined;
};

export const useBoeSubmitPromptHandler = (): RovoContentBridgeSetupHandler => {
	const chatContextActions = useChatContextStoreActions();
	const bindHandlersCallback: RovoContentBridgeSetupHandler = useCallback(
		(on) => {
			on(BoeSubmitPrompt, (data) => {
				const bridgeAPI = getBrowserBridgeAPI();
				const CONFLUENCE_AFM_CLIENT_ID = 'confluence-afm' as ClientId;

				// Register the staged content as the 'staging-area' target so
				// AFM's `populateSendMessageContext` emits whiteboard/database context.
				// Inferring contentType from senderId keeps this AFM-only (no canvas change).
				const contentId = data.contentId || '';
				const contentType = inferContentTypeFromSenderId(data.senderId);
				if (contentId && contentType) {
					chatContextActions.setTarget('staging-area', {
						id: contentId,
						type: contentType,
						canProvideContext: true,
					});
				}

				bridgeAPI.send(InlineInputBoxSendMessageCommand, {
					senderId: CONFLUENCE_AFM_CLIENT_ID,
					receiverId: ROVO_CHAT_CLIENT_ID,
					prompt: data.prompt,
					source: 'CONFLUENCE_CWR_BLANK_OBJECT',
					productKey: 'confluence',
					deepResearchEnabled: false,
					webSearchEnabled: false,
					contentId,
					openChat: false,
					shouldStartNewConversation: true,
					addCurrentPageAsContextUserPreference: true,
					contentContext: 'staging-area',
					creationContextParams: {
						shouldUseExistingContent: true,
						experience: 'cwr_existing' as const,
						// Pass contentTypes so the backend knows which content type is being created.
						...(contentType ? { contentTypes: [contentType] } : {}),
					},
				});
			});
		},
		[chatContextActions],
	);

	return bindHandlersCallback;
};
